import { chakra } from '@chakra-ui/react';
import { getColorValue } from '@shinetools/sunshine';
import styled, { ThemeProps } from 'styled-components';

import Text from 'components/_core/Text';
import { LegacyTheme } from 'theme/legacyTheme';

import { StyleUploadProps } from '../../types';
import { OnDragContentProps } from './types';

type HelperProps = StyleUploadProps & ThemeProps<LegacyTheme>;

const getContainerBorderRadius = ({
  compactStyle,
  theme,
  withFlatBottom,
}: HelperProps) => {
  const borderRadius = compactStyle
    ? theme.spacings.abs8
    : theme.spacings.abs16;
  if (withFlatBottom) {
    return `${borderRadius}px ${borderRadius}px 0 0`;
  }
  return `${borderRadius}px`;
};

const getOnDragContentDistanceFromEdge = ({
  compactStyle,
  theme,
}: HelperProps) => (compactStyle ? 0 : theme.spacings.abs16);

const getOnDragContentPosition = (props: HelperProps) => {
  const distanceFromEdge = getOnDragContentDistanceFromEdge(props);
  return `top: ${distanceFromEdge}px;
  left: ${distanceFromEdge}px;

  width: calc(100% - ${distanceFromEdge * 2}px);
  height: calc(100% - ${distanceFromEdge * 2}px);`;
};

const getContainerPadding = (props: HelperProps) => {
  // The padding on the container matches onDragContentDistanceFromEdge so that
  // a permanent dashed line on the ContentWrapper will align with the OnDragContent.
  const padding = getOnDragContentDistanceFromEdge(props);
  return `${padding}px`;
};

const getOnDragContentBorderRadius = ({ compactStyle }: HelperProps) =>
  compactStyle ? '6px' : '12px';
const getContentWrapperPadding = (props: HelperProps) => {
  // The ContentWrapper's padding is calculated based on the distance to
  // the edge of the Container. However, the container has padding (the
  // same amount as onDragContentDistanceFromEdge) so this is subtracted.
  const { compactStyle, theme } = props;
  const distanceFromEdge = getOnDragContentDistanceFromEdge(props);
  const paddingFromOuterContainer = compactStyle ? 0 : theme.spacings.abs32;
  const actualPadding = paddingFromOuterContainer - distanceFromEdge;
  return `${actualPadding}px`;
};

const getDashedBorderStyle = (color: string) => `1px dashed ${color}`;

export const ContentWrapper = styled(chakra.div)`
  transition: all 150ms ease-out;
  padding: ${getContentWrapperPadding};
  border-radius: ${getOnDragContentBorderRadius};
`;

export const Container = styled(chakra.div)`
  position: relative;
  width: 100%;
  background-color: ${getColorValue('grey.200')};
  border-radius: ${getContainerBorderRadius};
  border: none;
  padding: ${getContainerPadding};
  cursor: pointer;
  text-align: left;
  & * {
    pointer-events: none;
  }
  &:hover ${ContentWrapper} {
    background-color: var(--colors-grey-300);
    border-color: var(--colors-grey-500);
  }
`;

export const OnDragContent = styled.div<OnDragContentProps>`
  position: absolute;
  ${getOnDragContentPosition};
  background-color: ${({ isOverDropZone }) =>
    isOverDropZone ? getColorValue('grey.400') : getColorValue('grey.300')};
  border: ${getDashedBorderStyle(getColorValue('grey.500'))};
  border-radius: ${getOnDragContentBorderRadius};
  opacity: ${({ dragging }): number => (dragging ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const OnDragContentText = styled(Text).attrs({
  fontWeight: 'weight-500',
  variant: 'primary',
})``;
